import { useEffect, useRef, useState } from "react";
import GuestTermsModal from "./GuestTermsModal";
import { setGuestTermsConsentRequester } from "./guestTermsGate";

/**
 * Mounts the consent modal once at the app shell and registers a Promise-
 * returning consent requester that the service-layer wrappers call when a
 * guest's upload RPC fails with FAILED_PRECONDITION/terms_required.
 *
 * The promise resolves with true after the user has agreed and the
 * acceptGuestTerms RPC has succeeded (so the next upload won't re-trip the
 * gate); it resolves with false if they cancelled the modal.
 *
 * Place this once near the top of the App tree, above any component that
 * could trigger an upload. No props.
 */
const GuestTermsGateProvider: React.FC = () => {
  const [open, setOpen] = useState(false);
  const pendingResolve = useRef<((accepted: boolean) => void) | null>(null);

  useEffect(() => {
    setGuestTermsConsentRequester(
      () =>
        new Promise<boolean>((resolve) => {
          pendingResolve.current = resolve;
          setOpen(true);
        })
    );
    return () => setGuestTermsConsentRequester(null);
  }, []);

  const handleAccepted = () => {
    setOpen(false);
    const r = pendingResolve.current;
    pendingResolve.current = null;
    r?.(true);
  };

  const handleCancel = () => {
    setOpen(false);
    const r = pendingResolve.current;
    pendingResolve.current = null;
    r?.(false);
  };

  return (
    <GuestTermsModal
      open={open}
      onAccepted={handleAccepted}
      onCancel={handleCancel}
    />
  );
};

export default GuestTermsGateProvider;
